// Slide geometry in px on the 960 x 540 slide. These mirror the backend
// Paginator and the .pptx renderers, so the preview draws what PowerPoint draws.
// Text is never measured here: every size, line count and table measure comes
// from the Paginator's output.
export const SLIDE_WIDTH = 960;
export const SLIDE_HEIGHT = 540;
export const MARGIN_X = 40;
export const CONTENT_WIDTH = SLIDE_WIDTH - 2 * MARGIN_X;
export const HEADER_TOP = 24;
// The header's top line: the section tag takes the start 60%, the as-at date the rest.
export const HEADER_TAG_WIDTH = Math.floor(CONTENT_WIDTH * 0.6);
export const HEADER_LINE_HEIGHT = 14;
export const TITLE_TOP = HEADER_TOP + HEADER_LINE_HEIGHT;
export const CONTENT_TOP = 112;
export const FOOTER_TOP = 508;
export const CONTENT_BOTTOM = FOOTER_TOP - 16;
// Every table's rows and header: 9 pt text, 16 px rows. Each line of text is
// TABLE_LINE_HEIGHTS px at its size, centred in the height the Paginator gave
// its row. The .pptx keeps 1 px above and below the text; here that px holds
// the collapsed cell borders instead, so cells have no vertical padding.
export const ROW_HEIGHT = 16;
export const HEADER_ROW_HEIGHT = 16;
export const TABLE_FONT_SIZE = 9;
export const TABLE_LINE_HEIGHTS = { 9: 14, 8: 13 };
export const CELL_PADDING = '0 6px';
// The " manual" marker after a status set by hand.
export const STATUS_MARKER_SIZE = 7;
export const TILES_HEIGHT = 72;
export const TILE_GAP = 12;
export const COMMENTARY_HEIGHT = 72;
export const BASIS_HEIGHT = 152;
// The basis lines BASIS_HEIGHT holds; a block without the contingency line is a line shorter.
export const BASIS_LINE_COUNT = 7;
export const BASIS_LINES_TOP = 32;
export const BASIS_LINE_HEIGHT = 16;
export const BASIS_LABEL_WIDTH = 180;
// The as-at note under the basis lines: 9 pt italics over every line it
// wraps to (`fit.noteLines`), BASIS_NOTE_GAP under the last line.
export const BASIS_NOTE_SIZE = 9;
export const BASIS_NOTE_GAP = 4;
// The status rules under the basis lines: a heading, the introduction in the
// slot the Paginator measured for it (`statusIntroHeight`), then each rule in
// its slot (`statusLineHeights`): its lines of 8 pt text, then a gap.
export const STATUS_HEADER_HEIGHT = 18;
export const STATUS_TEXT_LINE_HEIGHT = 13;
export const STATUS_LINE_GAP = 4;
export const CONTENTS_ENTRY_HEIGHT = 26;
export const CONTENTS_TAG_WIDTH = 165;
export const CONTENTS_NUMBER_WIDTH = 60;
// One entry of the appendix index.
export const APPENDIX_ENTRY_HEIGHT = 22;
export const APPENDIX_LETTER_WIDTH = 60;
// An organisation chart's node text, inside a box inset by TEXT_INSET, at the
// size OrgChartLayout fitted each node to.
export const ORG_CHART = {
    NODE_FONT_PT: 7,
    TEXT_INSET: 4,
};
// A table or chart caption, drawn in the top of its block.
export const CAPTION_HEIGHT = 20;
export const CAPTION_SIZE = 10;
export const NOTE_HEIGHT = 24;
// Each line a note wraps to past its first.
export const NOTE_LINE_HEIGHT = 15;

/**
 * A note block's height, as the Paginator's noteHeight() gives it from the
 * lines it fitted.
 */
export const noteHeight = (block) => NOTE_HEIGHT + (Math.max(1, Number(block.fit?.lines) || 1) - 1) * NOTE_LINE_HEIGHT;
// Assessment matrices, as the .pptx MatrixRenderer draws them: two grids side
// by side, each a title line, four cell rows, two lines of column labels and a
// footnote; and a column at the end of the block with the side table and the
// legend under it.
export const MATRIX = {
    HEIGHT: 108,
    GRID_WIDTH: 290,
    GRID_GAP: 16,
    LABEL_WIDTH: 62,
    TITLE_HEIGHT: 14,
    CELL_ROW_HEIGHT: 15,
    CELL_GAP: 2,
    COLUMN_LABEL_HEIGHT: 22,
    FOOTNOTE_HEIGHT: 12,
    SIDE_WIDTH: 260,
    SIDE_ROW_HEIGHT: 14,
    SIDE_MESSAGE_HEIGHT: 28,
    LEGEND_HEIGHT: 22,
    SIDE_LABEL_WIDTH: 80,
    SIDE_VALUE_WIDTH: 24,
    SIDE_NOTE_GAP: 8,
};

/**
 * The px a block's caption takes at its top: none without a caption.
 */
export const captionHeight = (block) => (block.caption ? CAPTION_HEIGHT : 0);

/**
 * Each status rule's slot, as the Paginator measured it.
 */
export const statusLineHeights = (block) =>
    (block.statusLines || []).map((line, index) => block.statusLineHeights?.[index] ?? STATUS_TEXT_LINE_HEIGHT + STATUS_LINE_GAP);

/**
 * The status rules introduction's slot, as the Paginator measured it: none
 * without one.
 */
export const statusIntroHeight = (block) => Number(block.statusIntroHeight) || 0;

/**
 * The "Status rules" heading, the introduction and each status rule's slot,
 * as the Paginator's statusRulesHeight() gives it.
 */
export const statusLinesHeight = (block) => statusLineHeights(block).reduce((height, slot) => height + slot, STATUS_HEADER_HEIGHT + statusIntroHeight(block));

/**
 * A point size as px, for text sized in pt by the .pptx renderers.
 */
export const pt = (size) => (size * 96) / 72;

/**
 * A line of text's px height at a point size, as the Paginator's lineHeight()
 * rounds it.
 */
export const lineHeight = (size) => Math.ceil(size * 1.2 * (96 / 72));

/**
 * Absolute placement of a box on the slide. The slide is always laid out left
 * to right, like the .pptx it previews.
 */
export const box = (x, y, width, height) => ({ position: 'absolute', left: x, top: y, width, height });
